import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, DownloadSimple, ArrowCounterClockwise, PaperPlaneRight, CheckCircle, XCircle, Scissors, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fetchBlobUrl, fileDownloadUrl } from "../lib/api";
import StageTracker from "../components/StageTracker";
import VideoPlayer from "../components/VideoPlayer";
import VideoABHarness from "../components/VideoABHarness";
import { useAuth } from "../context/AuthContext";

export default function VideoRunDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [run, setRun] = useState(null);
  const [vidUrl, setVidUrl] = useState(null);
  const [cmd, setCmd] = useState("");
  const [sending, setSending] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/video/runs/${id}`);
    setRun(data);
    return data;
  }, [id]);

  useEffect(() => { load().catch(() => toast.error("Run not found")); }, [load]);
  useEffect(() => {
    if (!run || run.status !== "running") return;
    const t = setInterval(load, 1800);
    return () => clearInterval(t);
  }, [run, load]);
  useEffect(() => {
    if (run?.output_path && run.status === "completed") {
      setVidUrl(null);
      fetchBlobUrl(run.output_path).then(setVidUrl).catch(() => {});
    }
  }, [run?.output_path, run?.status, run?.revisions?.length]);

  const retry = async () => { const { data } = await api.post(`/video/runs/${id}/retry`); toast.info(`Resuming from ${data.resumed_from}…`); load(); };
  const del = async () => {
    try { await api.delete(`/video/runs/${id}`); toast.success("Run deleted."); nav("/video/history"); }
    catch (e) { toast.error(e?.response?.status === 403 ? "Not allowed to delete this run." : "Delete failed."); }
  };
  const revise = async () => {
    if (!cmd.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/video/runs/${id}/revise`, { command: cmd });
      toast.success(`Re-running from “${data.resumed_from}”`);
      setCmd(""); load();
    } catch { toast.error("Revision failed."); }
    setSending(false);
  };

  if (!run) return <div className="font-mono text-muted-foreground">loading…</div>;
  const done = run.status === "completed";
  const retryable = run.status === "failed" || run.status === "interrupted";
  const canDelete = user && (user.role === "admin" || user.email === run.user_email);
  const plan = run.plan;

  return (
    <div>
      <button onClick={() => nav(-1)} className="flex items-center gap-2 font-mono text-xs text-muted-foreground hover:text-foreground transition-colors mb-6"><ArrowLeft size={14} /> back</button>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">{run.platform} · {run.target_len}s target · {run.id.slice(0, 8)}</div>
          <h1 className="font-mono font-extrabold tracking-tighter text-2xl">{run.original_filename}</h1>
        </div>
        <div className="flex items-center gap-3">
          <span data-testid="video-run-status" className="font-mono text-sm px-3 py-1.5 border border-border" style={{ color: done ? "#00E559" : retryable ? "#FF3333" : "#FF5500" }}>{run.status}{run.status === "failed" && run.failed_stage ? ` · ${run.failed_stage}` : ""}</span>
          {retryable && <button data-testid="video-retry" onClick={retry} className="flex items-center gap-2 font-mono text-sm bg-primary text-black px-3 py-1.5 hover:bg-[#FF7733] transition-colors"><ArrowCounterClockwise size={15} weight="bold" /> resume{run.failed_stage ? ` from ${run.failed_stage}` : ""}</button>}
          {canDelete && (confirmDel ? (
            <span className="flex items-center gap-2">
              <button data-testid="confirm-delete-video-run" onClick={del} className="font-mono text-sm bg-[#FF3333] text-white px-3 py-1.5 hover:bg-[#ff5555] transition-colors">confirm delete</button>
              <button data-testid="cancel-delete-video-run" onClick={() => setConfirmDel(false)} className="font-mono text-sm border border-border px-3 py-1.5 hover:bg-secondary transition-colors">cancel</button>
            </span>
          ) : (
            <button data-testid="delete-video-run-button" onClick={() => setConfirmDel(true)} className="flex items-center gap-2 font-mono text-sm border border-[#FF3333] text-[#FF3333] px-3 py-1.5 hover:bg-[#FF3333] hover:text-white transition-colors"><Trash size={15} /> delete</button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <div className="overline mb-4">pipeline · 6 stages</div>
          <StageTracker stages={run.stages} />

          {done && (
            <div className="mt-6 bg-[#18181B] border border-border p-5" data-testid="revision-panel">
              <div className="overline mb-3">revise · one conversational pass</div>
              <div className="flex gap-2">
                <input data-testid="revision-input" value={cmd} onChange={(e) => setCmd(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && revise()}
                  placeholder='"make the hook faster" · "captions smaller" · "keep speaker centered"'
                  className="flex-1 bg-background border border-border px-3 py-2.5 font-mono text-sm focus:border-primary outline-none transition-colors" />
                <button data-testid="revision-submit" disabled={sending} onClick={revise}
                  className="bg-primary text-black px-4 font-mono font-bold hover:bg-[#FF7733] transition-colors disabled:opacity-50"><PaperPlaneRight weight="fill" size={16} /></button>
              </div>
              <div className="text-xs text-muted-foreground mt-2 font-mono">Gemini routes your note to the right stage and re-runs only from there.</div>
              {run.revisions?.length > 0 && (
                <div className="mt-4 space-y-1.5">
                  {run.revisions.map((r, i) => (
                    <div key={i} className="font-mono text-xs flex items-center gap-2" data-testid={`revision-${i}`}>
                      <Scissors size={12} className="text-primary" /> <span className="text-muted-foreground">[{r.stage}]</span> {r.command}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-8">
          {done && (
            <div>
              <div className="overline mb-4">publish-ready cut · 1080×1920</div>
              {vidUrl ? <VideoPlayer src={vidUrl} testid="output-video" /> : <div className="bg-[#18181B] border border-border p-10 font-mono text-sm text-muted-foreground text-center">rendering preview…</div>}
            </div>
          )}

          {run.script && (
            <div>
              <div className="overline mb-3">generated script · {run.script.scenes?.length} scenes</div>
              <div className="bg-[#18181B] border border-border p-4">
                <div className="text-sm mb-3">🎬 Hook: {run.script.hook}</div>
                <div className="border-t border-border pt-3 space-y-2 max-h-64 overflow-auto">
                  {run.script.scenes?.map((sc, i) => (
                    <div key={i} className="font-mono text-xs" data-testid={`scene-${i}`}>
                      <span className="text-primary">#{i + 1}</span> {sc.narration}
                      <div className="text-muted-foreground mt-0.5">🖼 {sc.visual_prompt}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {plan && (
            <div>
              <div className="overline mb-3">edit plan</div>
              <div className="bg-[#18181B] border border-border p-4">
                <div className="text-sm mb-3">{plan.rationale}</div>
                <div className="font-mono text-xs text-muted-foreground">hook: segment #{plan.hook_index} · kept {plan.keep_indices?.length} segments · captions {plan.caption_size?.toUpperCase()}</div>
                {run.transcript && (
                  <div className="mt-3 border-t border-border pt-3 space-y-1.5 max-h-56 overflow-auto">
                    {plan.keep_indices?.map((idx, k) => (
                      <div key={idx} className="font-mono text-xs" data-testid={`edl-${k}`}>
                        <span className="text-primary">{k === 0 ? "HOOK" : `#${k + 1}`}</span>{" "}
                        <span className="text-muted-foreground">{run.transcript[idx]?.start?.toFixed(1)}–{run.transcript[idx]?.end?.toFixed(1)}s</span>{" "}
                        {run.transcript[idx]?.text}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {run.qc && (
            <div>
              <div className="overline mb-3">quality check</div>
              <div className="border border-border" data-testid="qc-panel">
                {Object.entries(run.qc).filter(([k]) => k !== "passed").map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between p-3 border-b border-border last:border-b-0 bg-[#18181B] font-mono text-xs">
                    <span className="text-muted-foreground">{k.replace(/_/g, " ")}</span>
                    <span>{typeof v === "boolean" ? (v ? <CheckCircle weight="fill" color="#00E559" size={16} /> : <XCircle weight="fill" color="#FF3333" size={16} />) : String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {done && (
            <div>
              <div className="overline mb-3">artifacts</div>
              <div className="border border-border">
                <a data-testid="dl-video" href={fileDownloadUrl(run.output_path)} target="_blank" rel="noreferrer" className="flex items-center justify-between p-4 border-b border-border bg-[#18181B] hover:bg-secondary transition-colors">
                  <div><div className="font-mono text-sm">final.mp4</div><div className="text-xs text-muted-foreground">9:16 · {run.output_duration}s</div></div><DownloadSimple size={18} className="text-muted-foreground" />
                </a>
                {run.srt_path && <a data-testid="dl-srt" href={fileDownloadUrl(run.srt_path)} target="_blank" rel="noreferrer" className="flex items-center justify-between p-4 bg-[#18181B] hover:bg-secondary transition-colors">
                  <div><div className="font-mono text-sm">captions.srt</div><div className="text-xs text-muted-foreground">burned + sidecar</div></div><DownloadSimple size={18} className="text-muted-foreground" />
                </a>}
              </div>
            </div>
          )}
        </div>
      </div>

      {done && <VideoABHarness run={run} reload={load} />}
    </div>
  );
}
